// One game on the practice hub, in the same card as the Sanskrit practice page: icon tile, how many levels are open,
// the Hindi name, the English name, one line, and a footer with what the game trains and "Sets of 10". The whole card
// opens the game's numbered sets at its first open level; the set picker then switches levels. A game without any
// playable level says why, plainly, and is not a link at all. With topics chosen in the filter bar, the link carries
// the topics this game understands; a game that understands none of them is dimmed with a short note. With विकल्प
// सहित chosen, the games that read it say so and their link carries it.
// श्रवण also opens when the device can read Prakrit aloud, even before a teacher's recording exists.

import type { FC, ReactNode } from 'react'
import type { L } from '@/prakrit/lib/lessons/types'
import type { ModeInfo } from '@/prakrit/components/practice/practice-catalog'
import type { Level, ModeAvailability } from '@/prakrit/components/practice/practice-types'

import Link from 'next/link'
import { Filter, Lock, Volume2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Bi } from '@/prakrit/components/common/bi'
import { StatusBadge } from '@/prakrit/components/common/status-badge'
import { UNAVAILABLE, VOICE_MISSING } from '@/prakrit/components/practice/practice-catalog'
import { LEVELS } from '@/prakrit/components/practice/practice-types'
import { TOPIC_BY_ID, TOPIC_NOTE, practiceHref, usesVikalp } from '@/prakrit/components/practice/practice-topics'

interface ModeCardProps {
    info: ModeInfo
    availability: ModeAvailability
    kids: boolean
    /** What the game trains, shown in the card's footer. */
    group: L
    /** The chosen topics this game understands (empty: no filter, or none it understands). */
    topics?: string[]
    /** Topics are chosen but this game understands none of them. */
    dimmed?: boolean
    /** The device voice: a Devanagari voice exists, and the learner allows it. */
    voice?: { supported: boolean, enabled: boolean, onEnable: () => void }
    /** विकल्प सहित is chosen on the hub (only the games that read it pass it on). */
    vikalp?: boolean
}

const hiDigits = (value: number): string => String(value).replace(/[0-9]/g, d => String.fromCharCode(0x0966 + Number(d)))

const CARD = 'group flex h-full flex-col rounded-[1.75rem] border bg-card p-5 shadow-sm'

export const ModeCard: FC<ModeCardProps> = ({ info, availability, kids, group, topics = [], dimmed = false, voice, vikalp = false }): ReactNode => {
    const Icon = info.icon
    const canSpeak = Boolean(voice?.supported && voice.enabled)
    const levels: Record<Level, boolean> = { ...availability.levels }

    if (canSpeak && availability.voiceLevels) {
        for (const level of LEVELS) levels[level] = levels[level] || availability.voiceLevels[level]
    }

    const openLevels = LEVELS.filter(l => levels[l])
    const open = openLevels.length > 0
    const voiceWouldOpen = !open && Boolean(availability.voiceLevels && LEVELS.some(l => availability.voiceLevels?.[l]))
    const onlyVoice = open && canSpeak && !LEVELS.some(l => availability.levels[l])
    const notes = TOPIC_NOTE[info.mode]
    const topicNote = topics.map(id => TOPIC_BY_ID[id]?.group).find(g => g && notes?.[g])
    const vikalpOn = vikalp && usesVikalp(info.mode)
    const count = openLevels.length

    const body = (
        <>
            <div className='flex items-start justify-between gap-3'>
                <span
                    aria-hidden='true'
                    className={cn(
                        'flex size-12 shrink-0 items-center justify-center rounded-2xl',
                        open ? 'bg-saffron-soft text-saffron-strong' : 'bg-muted text-muted-foreground'
                    )}
                >
                    <Icon className='size-6' />
                </span>
                {open ? (
                    <span className='rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground'>
                        <Bi hi={`${hiDigits(count)} स्तर`} en={`${count} ${count === 1 ? 'level' : 'levels'}`} layout='inline' />
                    </span>
                ) : (
                    <span className='inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground'>
                        <Lock className='size-3.5' aria-hidden='true' />
                        <Bi hi='जल्द' en='Soon' layout='inline' />
                    </span>
                )}
            </div>

            <h3 className='mt-3 flex flex-col'>
                <span lang='hi' className='hindi text-xl font-semibold leading-snug text-foreground'>{info.name.hi}</span>
                <span lang='en' className='font-medium text-foreground/85'>{info.name.en}</span>
            </h3>

            <p className='mt-1 flex-1 text-sm text-muted-foreground'>
                {kids
                    ? <Bi hi={info.kidsLine.hi} en={info.kidsLine.en} enClassName='text-muted-foreground' />
                    : <Bi hi={info.line.hi} en={info.line.en} enClassName='text-muted-foreground' />}
            </p>

            {(topics.length > 0 || vikalpOn) && open && (
                <div className='mt-3 flex flex-col gap-1 rounded-2xl bg-saffron-soft/60 px-3 py-2 text-sm'>
                    {topics.length > 0 && (
                        <p className='flex flex-wrap items-center gap-1.5'>
                            <Filter className='size-4 shrink-0 text-saffron-strong' aria-hidden='true' />
                            <span className='font-semibold'>
                                <Bi hi='विषय' en='Topics' layout='inline' always />
                            </span>
                            <span aria-hidden='true'>:</span>
                            <span lang='hi' className='hindi'>{topics.map(id => TOPIC_BY_ID[id]?.label.hi).join(', ')}</span>
                        </p>
                    )}
                    {topics.length > 0 && topicNote && notes?.[topicNote] && (
                        <p className='text-xs text-muted-foreground'>
                            <Bi hi={notes[topicNote]?.hi ?? ''} en={notes[topicNote]?.en ?? ''} layout='inline' />
                        </p>
                    )}
                    {vikalpOn && (
                        <p className='flex flex-wrap items-center gap-1.5 text-xs'>
                            <StatusBadge kind='alt' />
                            <Bi hi='विकल्प रूप भी पूछे जाएँगे' en='variant forms included' layout='inline' enClassName='text-muted-foreground' />
                        </p>
                    )}
                </div>
            )}

            {dimmed && open && (
                <p className='mt-3 rounded-2xl bg-muted px-3 py-2 text-xs text-muted-foreground'>
                    <Bi hi='इस खेल में चुने गए विषय नहीं हैं; यहाँ वे लागू नहीं होंगे।' en='This game has none of the chosen topics; they do not apply here.' layout='inline' />
                </p>
            )}

            {onlyVoice && (
                <p className='mt-3 flex items-start gap-2 text-xs text-muted-foreground'>
                    <Volume2 className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    <Bi hi='रिकॉर्डिंग अभी नहीं; डिवाइस की आवाज़ पढ़कर सुनाएगी (उच्चारण अनुमानित)।' en='No recordings yet; your device voice reads aloud (approximate pronunciation).' />
                </p>
            )}

            {!open && (
                voiceWouldOpen && voice ? (
                    <div className='mt-3 flex flex-col gap-2 rounded-2xl bg-muted px-3 py-2 text-sm text-muted-foreground'>
                        <p className='flex items-start gap-2'>
                            <Lock className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                            <Bi
                                hi={VOICE_MISSING[voice.supported ? 'off' : 'none'].hi}
                                en={VOICE_MISSING[voice.supported ? 'off' : 'none'].en}
                                always
                            />
                        </p>
                        {voice.supported && !voice.enabled && (
                            <button
                                type='button'
                                onClick={voice.onEnable}
                                className='inline-flex min-h-tap items-center gap-2 self-start rounded-full border border-border bg-background px-4 font-medium text-foreground hover:border-saffron'
                            >
                                <Volume2 className='size-4 text-saffron-strong' aria-hidden='true' />
                                <Bi hi='डिवाइस की आवाज़ चालू करें' en='Turn the device voice on' layout='inline' always />
                            </button>
                        )}
                    </div>
                ) : (
                    <p className='mt-3 flex items-start gap-2 rounded-2xl bg-muted px-3 py-2 text-sm text-muted-foreground'>
                        <Lock className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                        <Bi
                            hi={UNAVAILABLE[availability.reason ?? 'no_verified'].hi}
                            en={UNAVAILABLE[availability.reason ?? 'no_verified'].en}
                            always
                        />
                    </p>
                )
            )}

            <div className='mt-4 flex items-center justify-between gap-3 text-xs text-muted-foreground'>
                <Bi hi={group.hi} en={group.en} layout='inline' />
                <Bi hi='१०-१० के सेट' en='Sets of 10' layout='inline' />
            </div>
        </>
    )

    if (!open) {
        return <div className={cn(CARD, 'border-dashed border-border bg-card/70')}>{body}</div>
    }

    return (
        <Link
            href={practiceHref(info.mode, openLevels[0], dimmed ? [] : topics, vikalpOn)}
            className={cn(
                CARD,
                'border-border transition-all hover:-translate-y-0.5 hover:border-saffron hover:shadow-md',
                'focus-visible:ring-2 focus-visible:ring-saffron/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none',
                dimmed && 'opacity-45 hover:translate-y-0 hover:opacity-100 focus-visible:opacity-100'
            )}
        >
            {body}
        </Link>
    )
}
